import { onDestroy } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";
import { createList, type ListResult } from "@lib/list";
import { createLoader } from "@lib/loader";

/**
 * Search over a paginated Webflow Collection List.
 *
 * Replaces Jetboost's `jetboost-list-search-input-*` on lists that only ever
 * used it for search — `/integrations/data-points` first, and the same shape
 * covers `/blog`, `/faq` and `/glossary`. For the faceted case (filters,
 * chips, sort) see `int-filter`, which is built on the same two libraries.
 *
 * Two things Jetboost was doing that this has to do too:
 *
 *  1. **A Collection List renders at most 100 items.** Data points number 410
 *     across five pages, so searching only what the server rendered would
 *     miss three quarters of them. `@lib/loader` walks Webflow's own
 *     `?<id>_page=N` links (~220KB encoded for the four extra pages), and
 *     never on first paint — only once the user shows intent.
 *
 *  2. **Pagination links only point forward.** The bug report arrived as
 *     `/integrations/data-points?b4c211f8_page=2`, where pages 1 and 3–5 are
 *     unreachable from the markup. `backfill` fetches the earlier pages by
 *     constructing their URLs, and items are re-ordered by page so the table
 *     stays alphabetical.
 *
 * ## Behaviour
 *
 * - **Idle is untouched.** No query means the server-rendered page and
 *   Webflow's own pagination, exactly as shipped. Nothing is hidden or
 *   reordered on boot.
 * - **A query takes over.** Native pagination hides, every loaded page is
 *   searched at once, and results stream in as the remaining pages land.
 * - **Clearing the query hands the page back**, current page and pagination
 *   included.
 * - **"Show more" reveals one CMS page at a time** where the page has such a
 *   button (`/blog`), and hides itself once there is nothing left.
 *
 * ## Markup contract
 *
 * Mount on any element containing the Collection List:
 * `data-module="cms-search"`.
 *
 * | Attribute              | On                        | Required |
 * |------------------------|---------------------------|----------|
 * | `cms-search="list"`    | the `.w-dyn-list`         | inferred |
 * | `cms-search="search"`  | the search `<input>`      | inferred |
 *
 * A list needs a search input or a show-more button, not necessarily both:
 * `/changelog` has only the button.
 * | `cms-search="reset"`   | a "clear search" button   | no       |
 * | `cms-search="more"`    | a "show more" button       | no       |
 * | `cms-search="empty"`   | an empty state            | no       |
 * | `cms-search="count"`   | result count text         | no       |
 *
 * Everything is inferred from the markup Jetboost already left behind, so a
 * page that still carries its `jetboost-*` classes needs no attributes at
 * all: the list is found by `.w-dyn-list`, and the search input, reset button
 * and count element by the `jetboost-list-search-input-<id>` /
 * `-search-reset-<id>` / `jetboost-total-items-<id>` classes matching one of
 * the list's own `jetboost-list-wrapper-<id>` classes — a list can carry
 * several, one per Jetboost feature.
 *
 * Several instances can share a page (`/blog` runs three), so mount each one
 * on its own `.w-dyn-list`.
 *
 * The input is looked up outside the mount element as a fallback — on
 * data-points the search sits in a hero `<section>` and the table lives in
 * `<main>`, with only `.page-wrapper` in common.
 *
 * ## Options (`data-*` on the mount element)
 *
 * | Attribute              | Default              | Effect                       |
 * |------------------------|----------------------|------------------------------|
 * | `data-batch`           | one CMS page         | items per "show more" click  |
 * | `data-title-selector`  | `h1,h2,h3,h4,h5`     | the field typo tolerance uses |
 * | `data-exclude-selector`| `.u-sr-only,[aria-hidden="true"]` | dropped from the searchable text |
 * | `data-param`           | `q`                  | URL query param; naming one also opts a multi-search page into syncing |
 * | `data-url-sync`        | on when the page has one search | `on` / `off` to force it |
 */

const SEARCH_DEBOUNCE = 150;

// Entrance for rows appearing in a result set. Skipped past this many rows —
// a stagger across 400 table rows reads as lag, not motion.
const ANIMATE_LIMIT = 40;
const IN_DURATION = 0.35;
const IN_STAGGER_MAX = 0.25;
const EASE = "power2.out";

const DEFAULT_TITLE = "h1,h2,h3,h4,h5";
// Link affordances repeated on every row ("Learn more about this data point")
// would otherwise match every query.
const DEFAULT_EXCLUDE = '.u-sr-only,[aria-hidden="true"],script,style';

const JB_WRAPPER = /^jetboost-list-wrapper-([a-z0-9]+)$/i;

const attr = (root: ParentNode, value: string) =>
  root.querySelector<HTMLElement>(`[cms-search="${value}"]`);

export default function (element: HTMLElement, dataset: DOMStringMap) {
  // ---- resolve markup --------------------------------------------------

  const listWrap =
    attr(element, "list") ??
    (element.classList.contains("w-dyn-list")
      ? element
      : element.querySelector<HTMLElement>(".w-dyn-list"));
  if (!listWrap) return;

  const found = listWrap.querySelector<HTMLElement>(".w-dyn-items");
  if (!found) return;
  const grid: HTMLElement = found;

  const initialCells = Array.from(
    grid.querySelectorAll<HTMLElement>(":scope > .w-dyn-item"),
  );
  if (!initialCells.length) return;

  /**
   * Jetboost keyed every hook on a list to the same short id, so the id is
   * what pairs an input with the list it drives.
   *
   * A list can carry more than one: `/blog`'s "Latest" wrapper is
   * `jetboost-list-wrapper-vkge jetboost-list-wrapper-e6go` — one id for the
   * pagination hook, another for the search hook — so all of them have to be
   * tried, not just the first.
   */
  const boostIds = Array.from(listWrap.classList)
    .map((c) => JB_WRAPPER.exec(c)?.[1])
    .filter(Boolean) as string[];

  /**
   * Find one of this list's controls.
   *
   * The search input is routinely a long way from the list it drives — a hero
   * section, a modal overlay — so the search widens past the mount element.
   * But `/blog` mounts three of these on one page, and a document-wide
   * attribute lookup there would happily hand the "Latest" list the overlay's
   * input. The Jetboost id is the precise pairing; the loose lookup is only
   * allowed when the page holds exactly one candidate.
   */
  function resolve(role: string, jbRole: string | null): HTMLElement | null {
    const scoped = attr(element, role);
    if (scoped) return scoped;

    if (jbRole) {
      for (const id of boostIds) {
        const paired = document.querySelector<HTMLElement>(
          `.jetboost-${jbRole}-${id}`,
        );
        if (paired) return paired;
      }
    }

    const loose = document.querySelectorAll<HTMLElement>(`[cms-search="${role}"]`);
    return loose.length === 1 ? loose[0]! : null;
  }

  const searchInput = resolve(
    "search",
    "list-search-input",
  ) as HTMLInputElement | null;

  const resetBtn = resolve("reset", "list-search-reset");
  const countEl = resolve("count", "total-items");

  /**
   * "Show more" — Jetboost's load-more control, and on `/blog` the only
   * pagination the visitor can see: the native `.w-pagination-next` is hidden
   * by the site and this `href="#"` button sits outside the list entirely.
   * With Jetboost gone it does nothing, so it has to be taken over alongside
   * the search rather than left dead next to a working one.
   */
  const moreBtn = resolve("more", "pagination-next");

  // A list can be here for either control. `/glossary` has two lists and one
  // search box between them, and `/changelog` has no search at all — just a
  // Show More that JetBoost used to drive. Only bail when there's neither.
  if (!searchInput && !moreBtn) {
    console.warn(
      "[cms-search] no search input and no show-more button for",
      listWrap,
    );
    return;
  }
  const countInitial = countEl?.textContent ?? "";

  const paginationEl = listWrap.querySelector<HTMLElement>(
    ".w-pagination-wrapper",
  );

  const titleSelector = dataset.titleSelector || DEFAULT_TITLE;
  const excludeSelector = dataset.excludeSelector || DEFAULT_EXCLUDE;
  const queryParam = dataset.param || "q";

  /**
   * URL sync is on for a page with one search on it, and off by default when
   * there are several.
   *
   * `/blog` mounts three — an overlay, "Latest" and "More posts". Sharing one
   * `?q=` would have them overwriting each other on every keystroke and all
   * three restoring the same query on load. Rather than invent a param name
   * per instance, only sync when it's unambiguous; naming one with
   * `data-param` opts that list back in, and `data-url-sync="on"` forces it.
   */
  const instanceCount = document.querySelectorAll(
    '[data-module="cms-search"]',
  ).length;
  const syncUrl = !searchInput
    ? false // nothing to put in the URL
    : dataset.urlSync === "off"
      ? false
      : dataset.urlSync === "on" || !!dataset.param || instanceCount <= 1;

  // ---- searchable text --------------------------------------------------

  /**
   * The row's text minus the parts that are on every row. `textContent`
   * would fold in screen-reader link labels, so a query for "learn" would
   * return the whole catalogue.
   */
  function visibleText(el: HTMLElement): string {
    let out = "";
    const walk = (node: Node) => {
      const children = node.childNodes;
      for (let i = 0; i < children.length; i++) {
        const child = children[i]!;
        if (child.nodeType === Node.TEXT_NODE) {
          out += child.nodeValue;
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const e = child as HTMLElement;
          if (e.matches(excludeSelector)) continue;
          walk(e);
        }
      }
    };
    walk(el);
    return out.replace(/\s+/g, " ").trim().toLowerCase();
  }

  const derive = (el: HTMLElement) => ({
    title: (el.querySelector(titleSelector)?.textContent ?? "")
      .trim()
      .toLowerCase(),
    text: visibleText(el),
  });

  // ---- state ------------------------------------------------------------

  /** Sort key per cell — page index first, then position within the page, so
   *  a backfilled page 1 lands above the page the visitor arrived on. */
  const order = new Map<HTMLElement, number>();
  const cells = new Set<HTMLElement>(initialCells);

  let ready = false;
  let allLoaded = false;
  const canPaginate = () => !!scopeSelector && loader.hasMore;

  /**
   * How many more items a "show more" click reveals.
   *
   * A CMS page by default, which is what a click means on a paginated list.
   * `/blog-tag/*` is the other case: every post fits in one Collection List,
   * so there's nothing to fetch and the button is dead weight — `data-batch`
   * turns it back into a real control by revealing in smaller steps.
   */
  const batchSize = Math.max(1, Number(dataset.batch) || initialCells.length);

  /** How much of the collection the visitor has asked to see. Only moves on a
   *  "show more" click — a search loads pages invisibly and must not leave
   *  them on screen once the query is cleared. */
  let revealedCount = moreBtn
    ? Math.min(batchSize, initialCells.length)
    : initialCells.length;

  /** What the list shows with no query running. Without a "show more" button
   *  that's whatever the server rendered — including on `?…_page=3`, where
   *  slicing from the top of the collection would show the wrong page. */
  const idleCells = () =>
    moreBtn ? ordered().slice(0, revealedCount) : initialCells;
  let loadingAll = false;
  let visible = new Set<HTMLElement>(initialCells);

  const list = createList(initialCells, {
    statusAttr: "data-cms-status",
    derive,
    // Typo tolerance on the title only — a subsequence match against a
    // paragraph of description matches nearly everything — and only once an
    // exact search has come back empty, so "email" returns the twelve rows
    // that say email rather than burying them under loose title matches.
    fuzzyFields: ["title"],
    fuzzyMode: "fallback",
    onChange: (result) => {
      if (ready) handleChange(result);
    },
  });

  const query = () => list.searchQuery;

  // ---- pagination -------------------------------------------------------

  // Resolved before the loader so an unaddressable list degrades to
  // "search what's on screen" instead of scraping another list's rows.
  const scopeSelector = gridPath();

  const loader = createLoader({
    container: grid,
    scopeSelector,
    itemSelector: ":scope > .w-dyn-item",
    // Webflow's own pagination stays usable until a query takes over.
    hideNative: false,
    backfill: true,
    onLoad: (incoming, { page }) => {
      incoming.forEach((el, i) => {
        order.set(el, page * 1000 + i);
        el.style.display = "none"; // render() decides when it paints
        cells.add(el);
      });
      grid.append(...incoming);
      reorder();
      list.addItems(incoming);
    },
    onError: (err) => console.warn("[cms-search]", err.message),
  });

  initialCells.forEach((el, i) =>
    order.set(el, loader.currentPage * 1000 + i),
  );

  /**
   * How the grid is addressed inside a *fetched* page.
   *
   * `.w-dyn-items` on its own matches every other Collection List in the
   * document — nav promos, the footer, the category chips on each row — and
   * `querySelector` would happily return the first of them. Webflow gives a
   * plain Collection List no class of its own, so the handle usually has to
   * come from its wrapper: `.jetboost-list-wrapper-nrd6` on data-points, or
   * a `cms-search="list"` attribute where one has been added.
   *
   * Every candidate is verified against *this* document first — it has to
   * resolve to exactly this grid and nothing else — so a selector that would
   * silently scrape the wrong list never reaches the fetch.
   */
  function gridPath(): string | undefined {
    const own = (el: HTMLElement, generic: string) => {
      const rest = Array.from(el.classList).filter((c) => c !== generic);
      return rest.length ? `.${rest.join(".")}` : `.${generic}`;
    };

    const gridSel = own(grid, "w-dyn-items");
    const candidates = [
      dataset.gridSelector,
      listWrap!.hasAttribute("cms-search") ? `[cms-search="list"] ${gridSel}` : null,
      `${own(listWrap!, "w-dyn-list")} ${gridSel}`,
      gridSel,
      domPath(grid),
    ].filter(Boolean) as string[];

    for (const sel of candidates) {
      let matches: NodeListOf<Element>;
      try {
        matches = document.querySelectorAll(sel);
      } catch {
        continue; // a class with a character CSS won't take
      }
      if (matches.length === 1 && matches[0] === grid) return sel;
    }

    // Nothing addresses this list uniquely. Searching what the server
    // rendered still beats a search that quietly returns another list's rows.
    console.warn(
      "[cms-search] can't address this list in a fetched page — searching " +
        "the current page only. Add cms-search=\"list\" to the Collection " +
        "List, or a class of its own.",
      listWrap,
    );
    return undefined;
  }

  /** Structural fallback for a list with no distinguishing class. */
  function domPath(el: Element): string {
    const parts: string[] = [];
    let node: Element | null = el;
    while (node && node !== document.body && node.parentElement) {
      const i = Array.prototype.indexOf.call(node.parentElement.children, node);
      parts.unshift(`${node.tagName.toLowerCase()}:nth-child(${i + 1})`);
      node = node.parentElement;
    }
    return `body > ${parts.join(" > ")}`;
  }

  const ordered = () =>
    [...cells].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));

  /** Put the grid back in CMS order after a page arrives out of sequence. */
  function reorder() {
    grid.append(...ordered());
  }

  // ---- render ------------------------------------------------------------

  function render(show: HTMLElement[], animate = true) {
    const showSet = new Set(show);
    const entering: HTMLElement[] = [];

    for (const el of visible) {
      if (!showSet.has(el)) {
        gsap.killTweensOf(el);
        gsap.set(el, { clearProps: "opacity" });
        el.style.display = "none";
      }
    }

    for (const el of show) {
      if (!visible.has(el)) {
        el.style.display = "";
        entering.push(el);
      }
    }

    visible = showSet;

    if (entering.length && entering.length <= ANIMATE_LIMIT && animate && !reduced) {
      gsap.fromTo(
        entering,
        { opacity: 0 },
        {
          opacity: 1,
          duration: IN_DURATION,
          ease: EASE,
          stagger: IN_STAGGER_MAX / entering.length,
          overwrite: true,
        },
      );
    }
  }

  // ---- empty state --------------------------------------------------------

  // The page ships no empty state for this list, so filtering to nothing
  // would just blank the table. A Designer-authored `cms-search="empty"`
  // wins where one exists.
  const emptyEl =
    attr(element, "empty") ??
    (() => {
      const el = document.createElement("div");
      el.className = "cms-search_empty";
      el.setAttribute("role", "status");
      el.style.cssText = "display:none;padding:2rem 1rem;text-align:center";
      grid.after(el);
      return el;
    })();

  const ownsEmpty = !attr(element, "empty");

  function updateEmpty(result: ListResult) {
    const empty = !!query() && result.passing === 0 && allLoaded;
    if (empty && ownsEmpty) {
      emptyEl.textContent = `No results for “${searchInput?.value.trim() ?? query()}”.`;
    }
    emptyEl.style.display = empty ? "" : "none";
  }

  // ---- change handling ----------------------------------------------------

  function handleChange(result: ListResult) {
    const searching = !!query();

    // Idle hands the page back to Webflow: the visitor's own page, their
    // pagination, nothing rearranged.
    render(searching ? result.active : idleCells());

    if (paginationEl) paginationEl.style.display = searching ? "none" : "";
    // JetBoost's count elements are left showing a dead placeholder ("00
    // items" on /glossary), so reporting the live number is strictly better
    // than restoring it. Idle it counts what's loaded, which grows as "show
    // more" or a search pulls the remaining pages in.
    if (countEl) countEl.textContent = String(result.passing);
    syncMore();
    updateEmpty(result);
    if (syncUrl) writeUrl();

    if (searching && canPaginate()) void loadRemaining();
  }

  // ---- show more -----------------------------------------------------------

  /** Hidden while a query is running — a search already covers every page, so
   *  there is nothing left for the button to fetch. */
  function syncMore() {
    if (!moreBtn) return;
    const useful = !query() && (canPaginate() || revealedCount < cells.size);
    moreBtn.style.display = useful ? "" : "none";
  }

  let showingMore = false;

  async function showMore() {
    if (showingMore) return;
    showingMore = true;
    try {
      // Only fetch when the visitor has already seen everything in memory —
      // a search may have pulled the rest of the collection in already.
      if (revealedCount >= cells.size && canPaginate()) {
        element.setAttribute("data-cms-loading", "");
        await loader.load();
        allLoaded = !canPaginate();
        element.removeAttribute("data-cms-loading");
      }
      revealedCount = Math.min(cells.size, revealedCount + batchSize);
      if (!query()) render(idleCells());
      syncMore();
    } finally {
      showingMore = false;
    }
  }

  async function loadRemaining() {
    if (loadingAll || !canPaginate()) return;

    loadingAll = true;
    element.setAttribute("data-cms-loading", "");
    // Each page calls back into list.addItems → onChange → handleChange, so
    // results grow as they arrive rather than waiting on the slowest page.
    await loader.loadAll();
    loadingAll = false;
    allLoaded = !canPaginate();
    element.removeAttribute("data-cms-loading");

    updateEmpty(list.getResult());
  }

  // ---- URL state -----------------------------------------------------------

  // Stashed so clearing the query returns the visitor to the page they were
  // on, rather than silently to page 1.
  const pageParam = loader.pageParam;
  const pageValue = pageParam
    ? new URLSearchParams(location.search).get(pageParam)
    : null;

  function writeUrl() {
    const params = new URLSearchParams(location.search);
    const q = query();

    if (q) {
      // Results span every page, so a page number would be a lie.
      for (const key of [...params.keys()]) {
        if (key.endsWith("_page")) params.delete(key);
      }
      params.set(queryParam, q);
    } else {
      params.delete(queryParam);
      if (pageParam && pageValue) params.set(pageParam, pageValue);
    }

    const qs = params.toString();
    history.replaceState(null, "", `${location.pathname}${qs ? `?${qs}` : ""}`);
  }

  // ---- wiring ---------------------------------------------------------------

  const unsubs: Array<() => void> = [];

  const on = (
    target: EventTarget,
    type: string,
    handler: EventListenerOrEventListenerObject,
    opts?: AddEventListenerOptions,
  ) => {
    target.addEventListener(type, handler, opts);
    unsubs.push(() => target.removeEventListener(type, handler, opts));
  };

  let timer: ReturnType<typeof setTimeout>;

  const search = () => {
    clearTimeout(timer);
    list.search(searchInput?.value.trim() ?? "", ["title", "text"]);
  };

  if (searchInput) {
    on(searchInput, "input", () => {
      clearTimeout(timer);
      timer = setTimeout(search, SEARCH_DEBOUNCE);
    });
    unsubs.push(() => clearTimeout(timer));
  }

  // The input sits in a Webflow form, so Enter submits it — which is the
  // reported bug: the form posts, Turnstile fires, and the visitor gets
  // "Oops! Something went wrong while submitting the form."
  //
  // Caught on keydown rather than on submit alone: implicit submission never
  // starts, so Webflow's own delegated submit handler never runs either and
  // the fix doesn't rest on which listener happens to be bound first. The
  // submit guard stays as a backstop for a form with a real submit button.
  if (searchInput) {
    on(searchInput, "keydown", (e) => {
      if ((e as KeyboardEvent).key !== "Enter") return;
      e.preventDefault();
      search();
    });

    const form = searchInput.closest("form");
    if (form) {
      on(form, "submit", (e) => {
        e.preventDefault();
        search();
      });
    }

    // Warm the next page on intent so the first keystroke doesn't wait on the
    // network.
    on(searchInput, "focus", () => loader.prefetch(), { once: true });
  }

  if (resetBtn && searchInput) {
    on(resetBtn, "click", (e) => {
      e.preventDefault();
      searchInput.value = "";
      search();
    });
  }

  if (moreBtn) {
    // href="#", so without this it just jumps the page to the top.
    on(moreBtn, "click", (e) => {
      e.preventDefault();
      void showMore();
    });
  }

  // ---- boot ------------------------------------------------------------------

  if (syncUrl && searchInput) {
    const initial = new URLSearchParams(location.search).get(queryParam);
    if (initial) {
      searchInput.value = initial;
      list.search(initial.trim().toLowerCase(), ["title", "text"]);
    }
  }

  ready = true;
  allLoaded = !canPaginate();

  {
    const result = list.getResult();
    // No entrance animation on boot — these rows are already painted.
    render(query() ? result.active : idleCells(), false);
    syncMore();
    if (paginationEl && query()) paginationEl.style.display = "none";
    if (countEl) countEl.textContent = String(result.passing);
    updateEmpty(result);
    if (query() && canPaginate()) void loadRemaining();
  }

  onDestroy(() => {
    unsubs.forEach((off) => off());
    gsap.killTweensOf([...cells]);
    if (ownsEmpty) emptyEl.remove();
  });
}
